import {
  CONSTRUCTION,
  DATE_FROM,
  DATE_MAX,
  DATE_MIN,
  DATE_TO,
  EDIT_NO,
  EDIT_YES,
  ENDPOINT_SERVICE_URL,
  ERROR_IN_FETCH,
  GIT_VERSION,
  HIDE,
  JSON_PG,
  NOT_FOUND_QUERY_NAME,
  QUERY_BD,
  ROW_FIELDS_VALUE_PARAM,
  SAVE_TO_SERVER,
  SHOW,
  TRYING_TO_SAVE,
  TRYING_TO_FETCH,
  UPDATE_BASE_SUCCESS,
  VIEW_QUERY,
  VIEW_QUERY_NAME,
  VIEW_QUERY_WHERE,
  VIEW_QUERY_WHERE_DEFAULT,
  insertParameterValue,
  type QueryRecord,
  type StartEnv,
} from '@query/shared';
import { create } from 'zustand';
import type { ColDef, GridApi } from 'ag-grid-community';
import { checkPassw, defaultEndpoint, fetchArray, loadEnv, parseRowFieldsValue, saveRow } from './lib/api.js';
import { buildColumnDefs, inferColumns, lookupUrl, pickLookupValues, type GridRow } from './lib/columns.js';
import { buildRootMenu, type OpenMode } from './lib/menu.js';
import type { MenuNestedItem } from './components/NestedMenu.js';
import { getParameter } from './lib/url-params.js';
import { ROW_SELECT_DELAY_MS } from './constants.js';

/**
 * Состояние приложения — замена глобальных переменных из AppFC.kt/AppFCFun.kt
 * и карты сеттеров useEffectMap.
 *
 * Почему store, а не useState внутри компонента: в Kotlin-версии действия
 * (lunchQuery, refreshMenuItems, formGreed, addToHistory) — обычные функции,
 * вызываемые откуда угодно без проброски пропсов. Store сохраняет это свойство.
 */
export interface AppState {
  // конфигурация
  startEnv: StartEnv;
  endpointServiceUrl: string;
  /** Имя представления со списком запросов (из него строится urlQuerys). */
  viewQueryName: string;
  urlQuerys: string;
  /** Текущий адрес загруженного представления. */
  urlInput: string;

  // данные
  queriesLoaded: QueryRecord[];
  titlesList: QueryRecord[];
  queryCurrent: QueryRecord | null;
  rowList: GridRow[];
  colList: ColDef<GridRow>[];
  /** Состояние строк по индексу: upd / del / ins / copy — перенос карты NewValue. */
  newValue: Record<string, string>;
  /** API грида: хранится в store, как глобальная gridApi в оригинале. */
  gridApi: GridApi<GridRow> | null;

  // интерфейс
  showNav: string;
  showInput: string;
  showShare: string;
  showButton: string;
  editIcon: string;
  currentPlaceholder: string;
  titleCurrent: string;
  resultUpdate: string;
  adminState: boolean;
  /** Пароль администратора, введённый в меню Service; уходит заголовком при записи. */
  adminToken: string;
  newTab: boolean;
  historyDialogOpen: boolean;
  intervalDialogOpen: boolean;

  // история и параметры строки
  queryHistory: Array<[string, string, string]>;
  historyCurrent: number;
  rowFieldsValueMap: Record<string, string>;
  rowFieldsValueString: string;
  dateFrom: string;
  dateTo: string;

  // действия
  init: () => Promise<void>;
  refreshMenuItems: (load?: boolean, first?: boolean) => Promise<void>;
  lunchQuery: (input: string | null | undefined, fieldsValue?: string) => Promise<void>;
  formGreed: (query: string, fieldsValue: string) => Promise<void>;
  addToHistory: (query: string) => void;
  openFromMenu: (query: QueryRecord, mode: OpenMode) => void;
  setPlaceholder: (what: string) => void;
  submitInput: (input: string) => Promise<void>;
  goHistory: (delta: number) => Promise<void>;
  openHistoryEntry: (index: number) => Promise<void>;
  historyEntryUrl: (index: number) => string;
  toggleNewTab: () => void;
  setHistoryDialogOpen: (open: boolean) => void;
  setIntervalDialogOpen: (open: boolean) => void;
  setDateFrom: (value: string) => void;
  setDateTo: (value: string) => void;
  toggleShowShare: () => void;
  setTitle: (title: string) => void;
  setResultUpdate: (text: string) => void;
  setGridApi: (api: GridApi<GridRow> | null) => void;

  // редактирование
  toggleEdit: () => void;
  setCellValue: (nodeId: string | undefined, rowIndex: number, row: GridRow, newCellValue: unknown, oldValue: unknown, field: string) => void;
  deleteSelectedRows: () => void;
  copySelectedRows: () => Promise<void>;
  saveChanges: () => Promise<void>;
  addNewQuery: (title: string) => Promise<void>;
  refreshRow: () => Promise<void>;
  viewResult: (result: string) => Promise<void>;
  cellStateClass: (nodeId: string | undefined, columnCellClass: string | undefined) => string;
  /** Отметка выделения строки для значения valueGetter (значения с @@). */
  decodeCellValue: (value: unknown) => unknown;
  serviceMenuItems: () => MenuNestedItem[];
  menuItems: () => MenuNestedItem;
  /** Заголовок для share-ссылки: адрес текущего представления. */
  shareUrl: () => string;
}

export const useAppStore = create<AppState>((set, get) => ({
  startEnv: {},
  endpointServiceUrl: defaultEndpoint(),
  viewQueryName: VIEW_QUERY,
  urlQuerys: '',
  urlInput: '',

  queriesLoaded: [],
  titlesList: [],
  queryCurrent: null,
  rowList: [],
  colList: [],
  newValue: {},
  gridApi: null,

  showNav: HIDE,
  showInput: HIDE,
  showShare: HIDE,
  showButton: HIDE,
  editIcon: EDIT_NO,
  currentPlaceholder: '',
  titleCurrent: '',
  resultUpdate: '',
  adminState: false,
  adminToken: '',
  newTab: false,
  historyDialogOpen: false,
  intervalDialogOpen: false,

  queryHistory: [],
  historyCurrent: -1,
  rowFieldsValueMap: {},
  rowFieldsValueString: '',
  dateFrom: DATE_MIN,
  dateTo: DATE_MAX,

  /**
   * Инициализация — перенос useEffectOnce (AppFC.kt:83-102).
   * Обращение к серверу в две ступени: сначала адрес из параметра b= (или origin),
   * затем уточнение адреса значением из startEnv.
   */
  init: async () => {
    const endpointFromUrl = getParameter(ENDPOINT_SERVICE_URL);
    const firstEnv = await loadEnv(endpointFromUrl ?? defaultEndpoint());
    const endpointServiceUrl = endpointFromUrl ?? firstEnv[ENDPOINT_SERVICE_URL] ?? '';
    const startEnv = await loadEnv(endpointServiceUrl === '' ? defaultEndpoint() : endpointServiceUrl);

    const viewQueryName = getParameter(VIEW_QUERY_NAME) ?? startEnv[VIEW_QUERY_NAME] ?? VIEW_QUERY;
    const where =
      getParameter(VIEW_QUERY_WHERE) ?? startEnv[VIEW_QUERY_WHERE] ?? VIEW_QUERY_WHERE_DEFAULT;

    set({
      startEnv,
      endpointServiceUrl,
      viewQueryName,
      urlQuerys: `bd/${QUERY_BD}/jsonPG/${viewQueryName}/${where}`,
      showNav: startEnv[QUERY_BD] === undefined ? HIDE : (getParameter('m') ?? SHOW),
    });

    await get().refreshMenuItems(true, true);
  },

  /** Обновление списка запросов и меню — перенос refreshMenuItems (AppFCFun.kt:483-522). */
  refreshMenuItems: async (load = true, first = false) => {
    const state = get();
    const rowFieldsValueMap = {
      ...state.rowFieldsValueMap,
      [DATE_FROM]: state.dateFrom,
      [DATE_TO]: state.dateTo,
    };
    const rowFieldsValueString = JSON.stringify(rowFieldsValueMap);
    set({ rowFieldsValueMap, rowFieldsValueString });

    let queriesLoaded = state.queriesLoaded;

    if (load) {
      set({ resultUpdate: TRYING_TO_FETCH });
      const loaded = await fetchArray<QueryRecord>(
        state.urlQuerys,
        rowFieldsValueString,
        state.endpointServiceUrl,
      );
      if (loaded.length === 1) {
        // Одна строка означает ошибку (формат ответа fullWeb: массив из одного элемента).
        if (state.startEnv[QUERY_BD] !== undefined) {
          const errorText = String((loaded[0] as unknown as Record<string, unknown>)['error'] ?? '');
          window.alert(`loadMenuItems Error\n\n${errorText}`);
        }
      } else {
        queriesLoaded = loaded;
      }
      set({ resultUpdate: '' });
    }

    // Отсекаются строки-подключения (имена с хвостовым «_») и незаполненные параметры.
    const titlesList = queriesLoaded
      .filter((query) => !query.name.endsWith('_'))
      .map((query) => ({ ...query, heading: insertParameterValue(query.heading, rowFieldsValueMap) }))
      .filter((query) => !query.heading.includes('{'));

    set({ queriesLoaded, titlesList });

    if (first) {
      await get().lunchQuery(
        getParameter('q') ?? state.startEnv['q'],
        getParameter(ROW_FIELDS_VALUE_PARAM) ?? '',
      );
    }
  },

  /** Загрузка выбранного запроса — перенос lunchQuery (AppFCFun.kt:345-382). */
  lunchQuery: async (input, fieldsValue) => {
    if (input === null || input === undefined || input.trim() === '') return;

    const state = get();
    const fieldsValueArg = fieldsValue ?? state.rowFieldsValueString;

    // Если в карте только даты (2 ключа) — берём rFV из адресной строки.
    const rowFieldsValueString =
      Object.keys(state.rowFieldsValueMap).length === 2
        ? (getParameter(ROW_FIELDS_VALUE_PARAM) ?? fieldsValueArg)
        : fieldsValueArg;
    const rowFieldsValueMap =
      rowFieldsValueString === ''
        ? state.rowFieldsValueMap
        : parseRowFieldsValue(rowFieldsValueString);

    set({
      rowFieldsValueString,
      rowFieldsValueMap,
      resultUpdate: TRYING_TO_FETCH,
      rowList: [],
      showButton: HIDE,
      queryCurrent: null,
    });

    let query = input;
    if (!input.includes('/')) {
      // Запрос по имени из списка (без «/» в адресе).
      const found = state.queriesLoaded.find((item) => item.name === input);
      if (found === undefined) {
        window.alert(`${ERROR_IN_FETCH} -> ${NOT_FOUND_QUERY_NAME} ${input}`);
        set({ resultUpdate: '' });
        return;
      }
      const current = found.name === VIEW_QUERY ? { ...found, query: state.urlQuerys } : found;
      const base = `${current.query}${current._sql !== null ? `?name=${current.name}` : ''}`;
      query = insertParameterValue(base, rowFieldsValueMap);
      set({ queryCurrent: current });
    }

    set({ urlInput: query });

    try {
      await get().formGreed(query, rowFieldsValueString);
      get().addToHistory(query);
    } finally {
      set({ resultUpdate: '' });
    }

    // Выделение первой строки после загрузки (AppFCFun.kt:379-380).
    // В оригинале это делалось через delay(500); здесь ждём появления данных.
    window.setTimeout(() => {
      get().gridApi?.getDisplayedRowAtIndex(0)?.setSelected(true);
    }, ROW_SELECT_DELAY_MS);
  },

  /** Загрузка строк представления — перенос formGreed (AppFCFun.kt:384-401). */
  formGreed: async (query, fieldsValue) => {
    const state = get();
    const rows = await fetchArray<GridRow>(query, fieldsValue, state.endpointServiceUrl);

    // Описание колонок строится ДО установки состояния: значения редакторов-списков
    // подгружаются с сервера, а это асинхронная операция.
    const { columnDefs, warnings } = await buildColumnDefs(rows, {
      columns: state.queryCurrent?.columns ?? '',
      cellStateClass: (nodeId, columnCellClass) => get().cellStateClass(nodeId, columnCellClass),
      loadLookupValues: async (view) => {
        const url = lookupUrl(query, view);
        const lookupRows = await fetchArray<Record<string, unknown>>(
          url,
          fieldsValue,
          state.endpointServiceUrl,
        );
        return pickLookupValues(lookupRows);
      },
    });
    for (const warning of warnings) console.warn(`[columns] ${warning}`);

    set({ rowList: rows, colList: columnDefs, editIcon: EDIT_NO, newValue: {} });

    // Кнопки правки показываются только для редактируемых представлений.
    const name = state.queryCurrent?.name;
    if (name === undefined) return;

    const currentQuery = state.queryCurrent?.query ?? '';
    const editable =
      (query.includes(JSON_PG) &&
        !currentQuery.includes(state.viewQueryName) &&
        !query.includes(state.viewQueryName) &&
        !name.endsWith('_ro')) ||
      ((state.viewQueryName.endsWith(name) || name.endsWith(state.viewQueryName)) && state.adminState);

    if (editable) set({ showButton: SHOW });
  },

  /** Запись в историю переходов — перенос addToHistory (AppFCFun.kt:403-420). */
  addToHistory: (query) => {
    const state = get();
    const queryName = state.queryCurrent?.name ?? query;
    const fromList = state.queriesLoaded.find((item) => item.name === state.queryCurrent?.name);
    const headerWithParam = insertParameterValue(
      fromList?.heading ?? query,
      state.rowFieldsValueMap,
    );

    const existing = state.queryHistory.findIndex(
      ([, name, heading]) => name === queryName && heading === headerWithParam,
    );

    if (existing >= 0) {
      set({ historyCurrent: existing });
      return;
    }

    const queryHistory: Array<[string, string, string]> = [
      ...state.queryHistory,
      [state.rowFieldsValueString, queryName, headerWithParam],
    ];
    set({ queryHistory, historyCurrent: queryHistory.length - 1, titleCurrent: headerWithParam });
  },

  /** Переход из меню — ветки newTab/link/обычная загрузка (AppFC.kt:173-183). */
  openFromMenu: (query, mode) => {
    const state = get();
    if (mode === 'link') {
      window.open(query.query);
      return;
    }
    if (mode === 'newTab') {
      const fields = state.rowFieldsValueString === '' ? '{}' : state.rowFieldsValueString;
      window.open(`${state.endpointServiceUrl}?q=${query.name}&${ROW_FIELDS_VALUE_PARAM}=${fields}`);
      return;
    }
    void get().lunchQuery(query.name);
  },

  /** Показ поля ввода с подсказкой — перенос setPlaceholder (AppFC.kt:193-197). */
  setPlaceholder: (what) => {
    set({ currentPlaceholder: what, showInput: get().showInput === HIDE ? SHOW : HIDE });
    window.setTimeout(() => {
      document.getElementById('inputAny')?.focus();
    }, 10);
  },

  /** Обработка ввода — перенос inputAny (AppFC.kt:119-142). */
  submitInput: async (input) => {
    const state = get();

    if (state.currentPlaceholder === ' Uri for query OR name query: ') {
      if (input.trim() !== '') await get().lunchQuery(input);
      else await get().refreshMenuItems();
    } else if (state.currentPlaceholder === ' Title for query') {
      // Добавление запроса в Construction — фаза 4 (нужен доступ к строкам грида).
      window.alert('Добавление запроса переносится в фазе 4');
    } else {
      const admin = await checkPassw(`bd/passw`, input, state.endpointServiceUrl);
      // Пароль запоминаем в памяти страницы: он же служит токеном мутаций (фаза 6).
      set({ adminState: admin, adminToken: admin ? input : '' });
    }

    set({ showInput: HIDE });
  },

  /** Переход по истории — перенос обработчиков prev/next (AppFC.kt:282-315). */
  goHistory: async (delta) => {
    const state = get();
    const next = state.historyCurrent + delta;
    if (next < 0 || next >= state.queryHistory.length) return;

    const entry = state.queryHistory[next]!;
    set({ historyCurrent: next });
    await get().lunchQuery(entry[1], entry[0]);
    set({ titleCurrent: entry[2] });
  },

  /** Переход к записи истории из диалога — перенос historyListDialog (AppFC.kt:199-243). */
  openHistoryEntry: async (index) => {
    const entry = get().queryHistory[index];
    if (entry === undefined) return;

    if (get().newTab) {
      window.open(get().historyEntryUrl(index));
    } else {
      await get().lunchQuery(entry[1], entry[0]);
    }
    set({ historyCurrent: index, titleCurrent: entry[2], historyDialogOpen: false });
  },

  /** Адрес для открытия записи истории в новой вкладке. */
  historyEntryUrl: (index) => {
    const entry = get().queryHistory[index];
    const fields = entry === undefined || entry[0] === '' ? '{}' : entry[0];
    return `${window.location.origin}?q=${entry?.[1] ?? ''}&${ROW_FIELDS_VALUE_PARAM}=${fields}`;
  },

  toggleNewTab: () => set({ newTab: !get().newTab }),
  setHistoryDialogOpen: (open) => set({ historyDialogOpen: open }),
  setIntervalDialogOpen: (open) => set({ intervalDialogOpen: open }),

  setDateFrom: (value) => {
    set({ dateFrom: value });
    void get().refreshMenuItems(false);
  },

  setDateTo: (value) => {
    set({ dateTo: value });
    void get().refreshMenuItems(false);
  },

  toggleShowShare: () => set({ showShare: get().showShare === SHOW ? HIDE : SHOW }),
  setTitle: (title) => set({ titleCurrent: title }),
  setResultUpdate: (text) => set({ resultUpdate: text }),
  setGridApi: (api) => set({ gridApi: api }),

  /** Переключение режима правки — перенос обработчика кнопки edit (AppFC.kt:340-353). */
  toggleEdit: () => set({ editIcon: get().editIcon === EDIT_NO ? EDIT_YES : EDIT_NO }),

  /** Класс ячейки — перенос getCellClass (AppFCFun.kt:219-227). */
  cellStateClass: (nodeId, columnCellClass) => {
    const state = get().newValue[nodeId ?? ''];
    const byState =
      state === 'upd'
        ? 'updatedClass'
        : state === 'del'
          ? 'deleteClass'
          : state === 'copy'
            ? 'copyClass'
            : state === 'ins'
              ? 'insertClass'
              : '';
    return `${byState} ${columnCellClass ?? ''}`;
  },

  /** Значение ячейки: строки с «@@» раскрываются (перенос getValueGetter, AppFCFun.kt:211-217). */
  decodeCellValue: (value) => {
    const text = String(value ?? '');
    if (!text.includes('@@')) return value;
    return text.replaceAll('@@', '');
  },

  /**
   * Изменение ячейки — перенос valueChanged (AppFCFun.kt:121-134).
   * Числовые колонки сохраняют тип: если прежнее значение было числом, новое тоже число.
   */
  setCellValue: (nodeId, rowIndex, row, newCellValue, oldValue, field) => {
    const isNumeric = oldValue !== undefined && (oldValue as { length?: number }).length === undefined;
    row[field] = isNumeric ? Number.parseFloat(String(newCellValue)) : String(newCellValue);

    const state = get();
    const rowList = [...state.rowList];

    const key = String(nodeId ?? '');

    // Строка кладётся по node.id, а НЕ по rowIndex (как `rowList[params.node.id]`
    // в оригинале, AppFCFun.kt:127). node.id — позиция в исходном массиве, rowIndex —
    // позиция на экране, и при сортировке они расходятся. Запись по rowIndex ставила
    // копию правимой строки на чужое место: появлялся дубль, а строка, лежавшая там,
    // пропадала. Заметно это стало на BigQuery, где первая колонка содержит NULL
    // и сортировка (sort: asc у первой колонки) сильно смещает порядок.
    // Пустой ключ даёт Number('') === 0, поэтому проверяем сам текст, а не результат.
    const targetIndex = /^\d+$/.test(key) ? Number(key) : rowIndex;
    rowList[targetIndex] = row;

    const newValue = { ...state.newValue };
    if (newValue[key] === undefined) newValue[key] = 'upd';
    else if (newValue[key] === 'copy') newValue[key] = 'ins';

    set({ rowList, newValue });
    state.gridApi?.redrawRows();
  },

  /** Удаление выделенных строк — перенос deleteRows (AppFCFun.kt:151-161). */
  deleteSelectedRows: () => {
    const state = get();
    const nodes = state.gridApi?.getSelectedNodes() ?? [];
    const newValue = { ...state.newValue };

    for (const node of nodes) {
      const key = String(node.id ?? '');
      if (newValue[key] === 'del') delete newValue[key];
      else newValue[key] = 'del';
    }

    set({ newValue });
    state.gridApi?.redrawRows();
  },

  /**
   * Копирование выделенных строк — перенос обработчика кнопки copy (AppFC.kt:363-385).
   * Дубликаты определяются сравнением JSON-представлений строк, как в оригинале.
   */
  copySelectedRows: async () => {
    const state = get();
    const selected = state.gridApi?.getSelectedRows() ?? [];
    if (selected.length === 0) return;

    const newValue = { ...state.newValue };
    selected.forEach((_, index) => {
      newValue[String(state.rowList.length + index)] = 'copy';
    });

    const all = await fetchArray<GridRow>(
      state.urlInput,
      state.rowFieldsValueString,
      state.endpointServiceUrl,
    );
    const selectedJson = selected.map((row) => JSON.stringify(row));
    const copies = all.filter((row) => selectedJson.includes(JSON.stringify(row)));

    set({ rowList: [...state.rowList, ...copies], newValue });
  },

  /** Перезагрузка текущего представления — перенос refreshRow (AppFCFun.kt:56-62). */
  refreshRow: async () => {
    const state = get();
    await get().formGreed(state.urlInput, state.rowFieldsValueString);
    set({ newValue: {} });
    state.gridApi?.redrawRows();
  },

  /** Показ результата сохранения — перенос viewResult (AppFCFun.kt:91-98). */
  viewResult: async (result) => {
    if (result.trim() === UPDATE_BASE_SUCCESS) {
      set({ resultUpdate: UPDATE_BASE_SUCCESS });
      window.setTimeout(() => set({ resultUpdate: '' }), 3000);
      return;
    }
    window.alert(result);
  },

  /** Сохранение изменений — перенос обработчика кнопки save (AppFC.kt:386-403). */
  saveChanges: async () => {
    const state = get();
    if (Object.keys(state.newValue).length === 0) return;
    if (!window.confirm(SAVE_TO_SERVER)) return;

    set({ resultUpdate: TRYING_TO_SAVE });
    const result = await saveRow(
      state.rowList,
      state.newValue,
      state.urlInput,
      state.endpointServiceUrl,
      state.rowFieldsValueMap,
      state.adminToken,
    );

    await get().refreshRow();
    await get().viewResult(result);
    if (state.urlInput.includes(state.viewQueryName)) await get().refreshMenuItems();
  },

  /** Добавление запроса в Construction — перенос addNewQuery (AppFCFun.kt:64-89). */
  addNewQuery: async (newTitle) => {
    const state = get();
    const fallbackName = state.urlInput.split('/').at(-1) ?? '';
    const name = newTitle !== '' ? newTitle : fallbackName;
    const columns = inferColumns(state.rowList[0] ?? {});

    const newQuery = {
      id: 0,
      name,
      query: state.urlInput,
      columns: columns.map((column) => `${column};\n`).join(''),
      heading: name,
      submenu: CONSTRUCTION,
      _sql: null,
    };

    set({ resultUpdate: TRYING_TO_SAVE });
    const result = await saveRow(
      [newQuery],
      { '0': 'ins' },
      state.urlQuerys,
      state.endpointServiceUrl,
      state.rowFieldsValueMap,
      state.adminToken,
    );
    await get().viewResult(result);
    await get().refreshMenuItems();
  },

  /** Пункты меню Service — перенос menuItemsCreate (AppFC.kt:414-424). */
  serviceMenuItems: () => {
    const state = get();
    return [
      { label: 'New Query', callback: () => get().setPlaceholder(' Uri for query OR name query: ') },
      { label: 'Add Query to Construction', callback: () => get().setPlaceholder(' Title for query') },
      { label: 'Admin passw', callback: () => get().setPlaceholder('Admin passw') },
      { label: state.urlInput, callback: () => void window.navigator.clipboard.writeText(state.urlInput) },
      { label: state.startEnv[GIT_VERSION] ?? '' },
    ];
  },

  menuItems: () =>
    buildRootMenu(get().titlesList, get().serviceMenuItems(), (query, mode) =>
      get().openFromMenu(query, mode),
    ),

  shareUrl: () => `${get().endpointServiceUrl}?q=${get().urlInput.replaceAll(' ', '%20')}`,
}));
